// Types for welcome questionnaires
export type QuestionType = "checkbox" | "text" | "scale" | "conditionalText" | "date";

export interface QuestionOption {
  id: string;
  label: string;
}

export interface Question {
  id: string;
  label: string;
  type: QuestionType;
  options?: QuestionOption[];
  scaleMin?: number;
  scaleMax?: number;
  required?: boolean;
  placeholder?: string;
  conditionalTextLabel?: string;
}

export interface QuestionnaireConfig {
  id: string;
  title: string;
  intro: string;
  questions: Question[];
  footer?: string;
}

// Questionnaire types based on service titles
export const QUESTIONNAIRE_TYPES = {
  NATUROPATHY_ADULTS: "naturopathy_adults",
  NATUROPATHY_CHILDREN: "naturopathy_children",
  BACH_FLOWERS: "bach_flowers",
  IRIDOLOGY: "iridology",
  DEFAULT: "default",
} as const;

export type QuestionnaireType = (typeof QUESTIONNAIRE_TYPES)[keyof typeof QUESTIONNAIRE_TYPES];

// Match service titles to questionnaire types
export function getQuestionnaireType(serviceTitle: string): QuestionnaireType {
  const title = serviceTitle.toLowerCase();

  if (
    title.includes("enfant") ||
    title.includes("ado") ||
    title.includes("famille") ||
    title.includes("courageuse") ||
    title.includes("parcours famille")
  ) {
    return QUESTIONNAIRE_TYPES.NATUROPATHY_CHILDREN;
  }
  if (
    title.includes("bach") ||
    title.includes("émotionnel") ||
    title.includes("emotionnel") ||
    title.includes("équilibre émotionnel")
  ) {
    return QUESTIONNAIRE_TYPES.BACH_FLOWERS;
  }
  if (title.includes("iridolog")) {
    return QUESTIONNAIRE_TYPES.IRIDOLOGY;
  }
  if (title.includes("naturopath") || title.includes("adulte")) {
    return QUESTIONNAIRE_TYPES.NATUROPATHY_ADULTS;
  }

  // Default to adults questionnaire for other services
  return QUESTIONNAIRE_TYPES.NATUROPATHY_ADULTS;
}

// Questionnaire configurations
export const QUESTIONNAIRE_CONFIGS: Record<QuestionnaireType, QuestionnaireConfig> = {
  [QUESTIONNAIRE_TYPES.NATUROPATHY_ADULTS]: {
    id: QUESTIONNAIRE_TYPES.NATUROPATHY_ADULTS,
    title: "Questionnaire de bienvenue",
    intro:
      "Merci de prendre 2 à 3 minutes pour répondre à ces quelques questions. Elles me permettront de préparer au mieux votre accompagnement.",
    questions: [
      {
        id: "motif_principal",
        label: "1. Quel est le motif principal de votre consultation ?",
        type: "checkbox",
        required: true,
        options: [
          { id: "fatigue", label: "Fatigue / manque d'énergie" },
          { id: "digestif", label: "Troubles digestifs" },
          { id: "stress", label: "Stress / émotions" },
          { id: "sommeil", label: "Troubles du sommeil" },
          { id: "douleurs", label: "Douleurs / inconforts" },
          { id: "prevention", label: "Accompagnement global / prévention" },
          { id: "autre", label: "Autre" },
        ],
      },
      {
        id: "motif_autre",
        label: 'Précisez "Autre"',
        type: "text",
        placeholder: "Précisez votre motif...",
      },
      {
        id: "attentes",
        label: "2. Qu'attendez-vous principalement de cette consultation ?",
        type: "text",
        required: true,
        placeholder: "Décrivez vos attentes...",
      },
      {
        id: "traitement_medical",
        label: "3. Suivez-vous actuellement un traitement médical ?",
        type: "conditionalText",
        conditionalTextLabel: "Lequel ?",
        options: [
          { id: "oui", label: "Oui" },
          { id: "non", label: "Non" },
        ],
      },
      {
        id: "pathologie_diagnostiquee",
        label: "4. Une pathologie a-t-elle été diagnostiquée par un professionnel de santé ?",
        type: "conditionalText",
        conditionalTextLabel: "Laquelle ?",
        options: [
          { id: "oui", label: "Oui" },
          { id: "non", label: "Non" },
        ],
      },
      {
        id: "niveau_energie",
        label: "5. Comment évaluez-vous votre niveau d'énergie actuellement ?",
        type: "scale",
        scaleMin: 1,
        scaleMax: 10,
        required: true,
      },
      {
        id: "comment_connu",
        label: "6. Comment m'avez-vous connue ?",
        type: "checkbox",
        required: true,
        options: [
          { id: "recommandation", label: "Recommandation" },
          { id: "reseaux_sociaux", label: "Réseaux sociaux" },
          { id: "internet", label: "Internet / site web" },
          { id: "autre", label: "Autre" },
        ],
      },
      {
        id: "comment_connu_autre",
        label: 'Précisez "Autre"',
        type: "text",
        placeholder: "Précisez...",
      },
    ],
    footer:
      "Les analyses biologiques récentes (moins de 3 mois) peuvent être utiles comme éléments d'observation dans le cadre de la consultation naturopathique, n'hésitez pas à me les apporter le jour du rendez-vous.",
  },

  [QUESTIONNAIRE_TYPES.NATUROPATHY_CHILDREN]: {
    id: QUESTIONNAIRE_TYPES.NATUROPATHY_CHILDREN,
    title: "Questionnaire de bienvenue - Enfant / Adolescent",
    intro:
      "À compléter par le parent. Merci de prendre quelques minutes pour répondre à ces questions. Elles me permettront de préparer au mieux le bilan de vitalité de votre enfant.",
    questions: [
      {
        id: "prenom_enfant",
        label: "1. Prénom de l'enfant",
        type: "text",
        required: true,
        placeholder: "Prénom",
      },
      {
        id: "date_naissance_enfant",
        label: "2. Date de naissance de l'enfant",
        type: "date",
        required: true,
        placeholder: "JJ/MM/AAAA",
      },
      {
        id: "motif_principal",
        label: "3. Motif principal de la consultation",
        type: "checkbox",
        required: true,
        options: [
          { id: "fatigue", label: "Fatigue" },
          { id: "digestif", label: "Troubles digestifs" },
          { id: "sommeil", label: "Sommeil" },
          { id: "stress", label: "Stress / émotions" },
          { id: "concentration", label: "Concentration / agitation" },
          { id: "immunite", label: "Immunité" },
          { id: "prevention", label: "Accompagnement global / prévention" },
          { id: "autre", label: "Autre" },
        ],
      },
      {
        id: "motif_autre",
        label: 'Précisez "Autre"',
        type: "text",
        placeholder: "Précisez votre motif...",
      },
      {
        id: "attentes",
        label: "4. Qu'attendez-vous principalement de cet accompagnement ?",
        type: "text",
        required: true,
        placeholder: "Décrivez vos attentes...",
      },
      {
        id: "traitement_medical",
        label: "5. Votre enfant suit-il actuellement un traitement médical ?",
        type: "conditionalText",
        conditionalTextLabel: "Lequel ?",
        options: [
          { id: "oui", label: "Oui" },
          { id: "non", label: "Non" },
        ],
      },
      {
        id: "pathologie_diagnostiquee",
        label: "6. Une pathologie a-t-elle été diagnostiquée par un professionnel de santé ?",
        type: "conditionalText",
        conditionalTextLabel: "Laquelle ?",
        options: [
          { id: "oui", label: "Oui" },
          { id: "non", label: "Non" },
        ],
      },
      {
        id: "niveau_energie",
        label: "7. Comment évaluez-vous le niveau d'énergie global de votre enfant ?",
        type: "scale",
        scaleMin: 1,
        scaleMax: 10,
        required: true,
      },
      {
        id: "antecedents_medicaux",
        label: "8. Antécédents médicaux notables (allergies, hospitalisations, maladies...)",
        type: "text",
        placeholder: "Maladies infantiles, hospitalisations, allergies...",
      },
      {
        id: "comment_connu",
        label: "9. Comment m'avez-vous connue ?",
        type: "checkbox",
        required: true,
        options: [
          { id: "recommandation", label: "Recommandation" },
          { id: "reseaux_sociaux", label: "Réseaux sociaux" },
          { id: "internet", label: "Internet / site web" },
          { id: "autre", label: "Autre" },
        ],
      },
      {
        id: "comment_connu_autre",
        label: 'Précisez "Autre"',
        type: "text",
        placeholder: "Précisez...",
      },
    ],
    footer:
      "Les analyses biologiques récentes (moins de 3 mois) peuvent être utiles comme éléments d'observation dans le cadre de la consultation naturopathique, n'hésitez pas à me les apporter le jour du rendez-vous.",
  },

  [QUESTIONNAIRE_TYPES.BACH_FLOWERS]: {
    id: QUESTIONNAIRE_TYPES.BACH_FLOWERS,
    title: "Questionnaire de bienvenue - Fleurs de Bach",
    intro: "Merci de répondre à ces quelques questions afin de préparer votre séance Fleurs de Bach.",
    questions: [
      {
        id: "consultation_pour",
        label: "1. Cette consultation est pour :",
        type: "checkbox",
        required: true,
        options: [
          { id: "adulte", label: "Un adulte (moi-même)" },
          { id: "enfant", label: "Un enfant / adolescent" },
        ],
      },
      {
        id: "prenom_enfant",
        label: "Prénom de l'enfant",
        type: "text",
        placeholder: "Prénom",
      },
      {
        id: "date_naissance_enfant",
        label: "Date de naissance de l'enfant",
        type: "date",
        placeholder: "JJ/MM/AAAA",
      },
      {
        id: "emotion_principale",
        label: "2. Quelle est l'émotion ou la difficulté principale que vous/votre enfant traverse actuellement ?",
        type: "text",
        required: true,
        placeholder: "Décrivez librement...",
      },
      {
        id: "duree_ressenti",
        label: "3. Depuis combien de temps ressentez-vous/ressent-il(elle) cela ?",
        type: "checkbox",
        required: true,
        options: [
          { id: "jours", label: "Quelques jours" },
          { id: "semaines", label: "Quelques semaines" },
          { id: "mois", label: "Plusieurs mois" },
          { id: "incertain", label: "Difficile à dire" },
        ],
      },
      {
        id: "impact_principal",
        label: "4. Cette situation impacte principalement :",
        type: "checkbox",
        required: true,
        options: [
          { id: "sommeil", label: "Le sommeil" },
          { id: "stress", label: "Le stress / l'anxiété" },
          { id: "emotions", label: "Les émotions (tristesse, colère, peurs…)" },
          { id: "confiance", label: "La confiance en soi" },
          { id: "relations", label: "Les relations" },
          { id: "autre", label: "Autre" },
        ],
      },
      {
        id: "impact_autre",
        label: 'Précisez "Autre"',
        type: "text",
        placeholder: "Précisez...",
      },
      {
        id: "deja_utilise_bach",
        label: "5. Avez-vous/A-t-il(elle) déjà utilisé les Fleurs de Bach ?",
        type: "checkbox",
        required: true,
        options: [
          { id: "oui", label: "Oui" },
          { id: "non", label: "Non" },
        ],
      },
      {
        id: "comment_connu",
        label: "6. Comment m'avez-vous connue ?",
        type: "checkbox",
        required: true,
        options: [
          { id: "recommandation", label: "Recommandation" },
          { id: "reseaux_sociaux", label: "Réseaux sociaux" },
          { id: "internet", label: "Internet / site web" },
          { id: "autre", label: "Autre" },
        ],
      },
      {
        id: "comment_connu_autre",
        label: 'Précisez "Autre"',
        type: "text",
        placeholder: "Précisez...",
      },
    ],
  },

  [QUESTIONNAIRE_TYPES.IRIDOLOGY]: {
    id: QUESTIONNAIRE_TYPES.IRIDOLOGY,
    title: "Questionnaire de bienvenue - Iridologie",
    intro: "Merci de répondre à ces quelques questions afin de préparer votre séance d'iridologie.",
    questions: [
      {
        id: "motif_principal",
        label: "1. Motif principal de votre consultation en iridologie",
        type: "text",
        required: true,
        placeholder: "Décrivez votre motif...",
      },
      {
        id: "deja_analyse_irido",
        label: "2. Avez-vous déjà réalisé une analyse iridologique ?",
        type: "checkbox",
        required: true,
        options: [
          { id: "oui", label: "Oui" },
          { id: "non", label: "Non" },
        ],
      },
      {
        id: "traitement_medical",
        label: "3. Suivez-vous actuellement un traitement médical ?",
        type: "conditionalText",
        conditionalTextLabel: "Lequel ?",
        options: [
          { id: "oui", label: "Oui" },
          { id: "non", label: "Non" },
        ],
      },
      {
        id: "fatigue_generale",
        label: "4. Ressentez-vous actuellement une fatigue générale ?",
        type: "checkbox",
        required: true,
        options: [
          { id: "oui", label: "Oui" },
          { id: "non", label: "Non" },
        ],
      },
      {
        id: "type_analyse",
        label: "5. Souhaitez-vous une analyse :",
        type: "checkbox",
        required: true,
        options: [
          { id: "preventive", label: "Préventive" },
          { id: "inconfort", label: "Axée sur un inconfort particulier" },
          { id: "global", label: "Dans le cadre d'un accompagnement global" },
        ],
      },
      {
        id: "comment_connu",
        label: "6. Comment m'avez-vous connue ?",
        type: "checkbox",
        required: true,
        options: [
          { id: "recommandation", label: "Recommandation" },
          { id: "reseaux_sociaux", label: "Réseaux sociaux" },
          { id: "internet", label: "Internet / site web" },
          { id: "autre", label: "Autre" },
        ],
      },
      {
        id: "comment_connu_autre",
        label: 'Précisez "Autre"',
        type: "text",
        placeholder: "Précisez...",
      },
    ],
  },

  [QUESTIONNAIRE_TYPES.DEFAULT]: {
    id: QUESTIONNAIRE_TYPES.DEFAULT,
    title: "Questionnaire de bienvenue",
    intro: "Merci de répondre à ces quelques questions afin de préparer votre consultation.",
    questions: [
      {
        id: "motif_principal",
        label: "1. Quel est le motif principal de votre consultation ?",
        type: "text",
        required: true,
        placeholder: "Décrivez votre motif...",
      },
      {
        id: "attentes",
        label: "2. Qu'attendez-vous de cette consultation ?",
        type: "text",
        required: true,
        placeholder: "Décrivez vos attentes...",
      },
      {
        id: "traitement_medical",
        label: "3. Suivez-vous actuellement un traitement médical ?",
        type: "conditionalText",
        conditionalTextLabel: "Lequel ?",
        options: [
          { id: "oui", label: "Oui" },
          { id: "non", label: "Non" },
        ],
      },
      {
        id: "comment_connu",
        label: "4. Comment m'avez-vous connue ?",
        type: "checkbox",
        required: true,
        options: [
          { id: "recommandation", label: "Recommandation" },
          { id: "reseaux_sociaux", label: "Réseaux sociaux" },
          { id: "internet", label: "Internet / site web" },
          { id: "autre", label: "Autre" },
        ],
      },
    ],
  },
};

// Helper to get label for a response value
export function getResponseLabel(question: Question, value: string | string[]): string {
  if (question.type === "checkbox" && question.options) {
    const values = Array.isArray(value) ? value : [value];
    return values.map((v) => question.options?.find((opt) => opt.id === v)?.label || v).join(", ");
  }
  if (question.type === "conditionalText" && question.options) {
    return question.options.find((opt) => opt.id === value)?.label || String(value);
  }
  return String(value);
}
